import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, 
  Check, 
  X, 
  Search, 
  RefreshCw, 
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  ChevronDown,
  Loader2,
  Plus,
  Pencil
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import './AdminCompanyManagement.scss';

interface Company {
  id: number;
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  cv_count: number;
  activity_sector?: string;
  created_at: string;
  approved_at?: string;
  approved_by_name?: string;
}

interface Stats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const AdminCompanyManagement: React.FC = () => {
  const { showToast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [sectorOptions, setSectorOptions] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [modalForm, setModalForm] = useState({ name: '', activity_sector: '' });

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await api.get('/companies', { params: { status } });
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/companies/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchStats();
  }, [fetchCompanies, fetchStats]);

  const handleApprove = async (id: number) => {
    try {
      await api.post(`/companies/${id}/approve`);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to approve company:', error);
    }
  };

  const handleReject = async (id: number) => {
    try {
      await api.post(`/companies/${id}/reject`);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to reject company:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šią įmonę?')) return;
    try {
      await api.delete(`/companies/${id}`);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete company:', error);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/companies/bulk-approve', { ids: selectedIds });
      setSelectedIds([]);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to bulk approve:', error);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/companies/bulk-reject', { ids: selectedIds });
      setSelectedIds([]);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to bulk reject:', error);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/companies/sync');
      if (response.data.success) {
        showToast(`Sinchronizuota: ${response.data.data.added} pridėta, ${response.data.data.updated} atnaujinta`, 'success');
        fetchCompanies();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to sync companies:', error);
      showToast('Nepavyko sinchronizuoti įmonių', 'error');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const loadSectors = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_URL}/cv-templates?grouped=true`);
        if (res.ok) {
          const result = await res.json();
          const fields = result.data?.work_field || [];
          setSectorOptions(fields.map((f: any) => f.value));
        }
      } catch { /* ignore */ }
    };
    loadSectors();
  }, []);

  const handleCreateCompany = async () => {
    if (!modalForm.name.trim()) {
      showToast('Įveskite įmonės pavadinimą', 'error');
      return;
    }
    try {
      await api.post('/companies', {
        name: modalForm.name.trim(),
        activity_sector: modalForm.activity_sector || null,
        status: 'approved'
      });
      showToast('Įmonė sukurta sėkmingai', 'success');
      setShowCreateModal(false);
      setModalForm({ name: '', activity_sector: '' });
      fetchCompanies();
      fetchStats();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Nepavyko sukurti įmonės';
      showToast(msg, 'error');
    }
  };

  const handleEditCompany = async () => {
    if (!editingCompany) return;
    try {
      await api.put(`/companies/${editingCompany.id}`, {
        name: modalForm.name.trim(),
        activity_sector: modalForm.activity_sector || null
      });
      showToast('Įmonė atnaujinta', 'success');
      setEditingCompany(null);
      setModalForm({ name: '', activity_sector: '' });
      fetchCompanies();
    } catch (error: any) {
      showToast('Nepavyko atnaujinti įmonės', 'error');
    }
  };

  const openEditModal = (company: Company) => {
    setEditingCompany(company);
    setModalForm({ name: company.name, activity_sector: company.activity_sector || '' });
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCompanies.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCompanies.map(c => c.id));
    }
  };

  const filteredCompanies = companies.filter(company => 
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved"><CheckCircle2 size={14} /> Patvirtinta</span>;
      case 'rejected':
        return <span className="status-badge rejected"><XCircle size={14} /> Atmesta</span>;
      default:
        return <span className="status-badge pending"><Clock size={14} /> Laukia</span>;
    }
  };

  return (
    <div className="admin-company-management">
      <div className="page-header">
        <div className="header-left">
          <Building2 size={28} />
          <div>
            <h1>Įmonių valdymas</h1>
            <p>Tvirtinkite ir valdykite įmones iš CV darbo patirties</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="create-btn"
            onClick={() => { setShowCreateModal(true); setModalForm({ name: '', activity_sector: '' }); }}
          >
            <Plus size={18} /> Pridėti įmonę
          </button>
          <button 
            className="sync-btn"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <><Loader2 size={18} className="spin" /> Sinchronizuojama...</>
            ) : (
              <><RefreshCw size={18} /> Sinchronizuoti iš CV</>
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Viso įmonių</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Laukia patvirtinimo</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-label">Patvirtinta</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Atmesta</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Ieškoti įmonės..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-dropdown">
          <button 
            className="filter-btn"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <Filter size={18} />
            {statusFilter === 'all' ? 'Visos' : 
             statusFilter === 'pending' ? 'Laukiančios' :
             statusFilter === 'approved' ? 'Patvirtintos' : 'Atmestos'}
            <ChevronDown size={16} />
          </button>
          {showFilterDropdown && (
            <div className="dropdown-menu">
              <button onClick={() => { setStatusFilter('all'); setShowFilterDropdown(false); }}>
                Visos
              </button>
              <button onClick={() => { setStatusFilter('pending'); setShowFilterDropdown(false); }}>
                Laukiančios
              </button>
              <button onClick={() => { setStatusFilter('approved'); setShowFilterDropdown(false); }}>
                Patvirtintos
              </button>
              <button onClick={() => { setStatusFilter('rejected'); setShowFilterDropdown(false); }}>
                Atmestos
              </button>
            </div>
          )}
        </div>

        {selectedIds.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedIds.length} pasirinkta</span>
            <button className="bulk-approve" onClick={handleBulkApprove}>
              <Check size={16} /> Patvirtinti
            </button>
            <button className="bulk-reject" onClick={handleBulkReject}>
              <X size={16} /> Atmesti
            </button>
          </div>
        )}
      </div>

      {/* Companies Table */}
      <div className="companies-table">
        {loading ? (
          <div className="loading">
            <Loader2 size={32} className="spin" />
            <p>Kraunama...</p>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <p>Įmonių nerasta</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox"
                    checked={selectedIds.length === filteredCompanies.length && filteredCompanies.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>ID</th>
                <th>Įmonės pavadinimas</th>
                <th>CV skaičius</th>
                <th>Veiklos sritis</th>
                <th>Statusas</th>
                <th>Sukurta</th>
                <th>Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map(company => (
                <tr key={company.id} className={selectedIds.includes(company.id) ? 'selected' : ''}>
                  <td>
                    <input 
                      type="checkbox"
                      checked={selectedIds.includes(company.id)}
                      onChange={() => toggleSelect(company.id)}
                    />
                  </td>
                  <td className="company-id">{company.id}</td>
                  <td className="company-name">{company.name}</td>
                  <td className="cv-count">{company.cv_count}</td>
                  <td className="sector">{company.activity_sector || '-'}</td>
                  <td>{getStatusBadge(company.status)}</td>
                  <td className="date">
                    {new Date(company.created_at).toLocaleDateString('lt-LT')}
                  </td>
                  <td className="actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => openEditModal(company)}
                      title="Redaguoti"
                    >
                      <Pencil size={16} />
                    </button>
                    {company.status === 'pending' && (
                      <>
                        <button 
                          className="action-btn approve"
                          onClick={() => handleApprove(company.id)}
                          title="Patvirtinti"
                        >
                          <Check size={16} />
                        </button>
                        <button 
                          className="action-btn reject"
                          onClick={() => handleReject(company.id)}
                          title="Atmesti"
                        >
                          <X size={16} />
                        </button>
                      </>
                    )}
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(company.id)}
                      title="Ištrinti"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Create / Edit Modal */}
      {(showCreateModal || editingCompany) && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); setEditingCompany(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCompany ? 'Redaguoti įmonę' : 'Pridėti naują įmonę'}</h3>
              <button className="modal-close" onClick={() => { setShowCreateModal(false); setEditingCompany(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <label className="form-label">
                Įmonės pavadinimas
                <input
                  type="text"
                  value={modalForm.name}
                  onChange={e => setModalForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="UAB Įmonė"
                  className="form-input"
                />
              </label>
              <label className="form-label">
                Veiklos sritis
                <select
                  value={modalForm.activity_sector}
                  onChange={e => setModalForm(prev => ({ ...prev, activity_sector: e.target.value }))}
                  className="form-input"
                >
                  <option value="">-- Pasirinkite --</option>
                  {sectorOptions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowCreateModal(false); setEditingCompany(null); }}>
                Atšaukti
              </button>
              <button className="btn-save" onClick={editingCompany ? handleEditCompany : handleCreateCompany}>
                {editingCompany ? 'Išsaugoti' : 'Sukurti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanyManagement;

